/*
 * @Author: Tommy Chen
 * @Date: 2025-05-19
 * @LastEditors: Tommy Chen
 * @LastEditTime: 2025-05-19
 * @FilePath: /cloud.admin.vue/src/utils/browserDeviceId.js
 * @Description: 浏览器设备ID生成工具.用于生成相对稳定的浏览器设备标识
 */

/**
 * 是否 Safari 浏览器
 */
const isSafari = /Version\/[\d\.]+.*Safari/.test(navigator.userAgent);

/**
 * 浏览器设备ID生成工具
 */
class BrowserDeviceId {
  static async generate() {
    // 1. 基础设备信息
    const basicInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: navigator.deviceMemory
    };

    // 2. Canvas 指纹
    const canvasFingerprint = this.getCanvasFingerprint();

    // 3. WebGL 指纹
    const webglFingerprint = this.getWebGLFingerprint();

    // 4. 音频指纹
    const audioFingerprint = await this.getAudioFingerprint();

    // 5. 组合所有信息并生成哈希
    const combinedInfo = {
      ...basicInfo,
      canvas: canvasFingerprint,
      webgl: webglFingerprint,
      audio: audioFingerprint
    };

    // 6. 生成设备ID
    const deviceId = this.hashString(JSON.stringify(combinedInfo));

    // 7. 存储到 localStorage 以便后续使用
    localStorage.setItem('browser_device_id', deviceId);

    return deviceId;
  }

  static getCanvasFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const txt = 'Browser Device ID';

    ctx.textBaseline = 'top';
    ctx.font = "14px 'Arial'";
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText(txt, 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText(txt, 4, 17);

    return canvas.toDataURL();
  }

  static getWebGLFingerprint() {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) {
      return null;
    }

    return {
      vendor: gl.getParameter(gl.VENDOR),
      renderer: gl.getParameter(gl.RENDERER),
      version: gl.getParameter(gl.VERSION)
    };
  }

  static async getAudioFingerprint() {
    /**
     * Safari 浏览器不支持 AudioContext 的创建
     */
    if (isSafari) {
      return null;
    }

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);

      analyser.fftSize = 256;
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(10000, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);

      oscillator.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.start(0);
      gainNode.gain.setValueAtTime(1, audioContext.currentTime);

      return new Promise(resolve => {
        scriptProcessor.onaudioprocess = e => {
          const data = new Float32Array(analyser.frequencyBinCount);
          analyser.getFloatFrequencyData(data);
          resolve(Array.from(data).join(','));
        };
      });
    } catch (e) {
      return null;
    }
  }

  static hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  // 获取设备ID（如果已存在则直接返回，否则生成新的）
  static async getDeviceId() {
    let deviceId = localStorage.getItem('browser_device_id');
    if (!deviceId) {
      deviceId = await this.generate();
    }
    return deviceId;
  }
}

// 导出工具函数
export const getDeviceId = async () => {
  try {
    const deviceId = await BrowserDeviceId.getDeviceId();
    return deviceId;
  } catch (error) {
    console.error('Failed to generate device ID:', error);
    return null;
  }
};

// 导出类（如果需要直接使用类）
export default BrowserDeviceId;
